import { downloadExcelWorkbook } from '../reports/exportExcelWorkbook';
import { formatLocalDate } from '../../components/common/customDatePicker.utils';
import type { ExpenseCategoryReportResult } from './types';

const fileDate = () => formatLocalDate(new Date());

export const exportExpenseCategoryReportExcel = async (
  report: ExpenseCategoryReportResult,
  filters: string[]
): Promise<void> => {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const summary = workbook.addWorksheet('Summary');
  summary.columns = [
    { key: 'category', width: 32 },
    { key: 'transactionCount', width: 15 },
    { key: 'amountEur', width: 18 },
    { key: 'percentage', width: 22 },
  ];
  filters.forEach((filter) => summary.addRow([filter]));
  summary.addRow([]);
  const header = summary.addRow([
    'Expense Category',
    'Transactions',
    'Amount EUR',
    '% of Total Expenses',
  ]);
  header.font = { bold: true };
  report.rows.forEach((row) =>
    summary.addRow({
      category: `${row.category}${row.isInactive ? ' (Inactive)' : ''}`,
      transactionCount: row.transactionCount,
      amountEur: row.amountEur,
      percentage: row.percentage / 100,
    })
  );
  const total = summary.addRow({
    category: 'TOTAL',
    transactionCount: report.totals.transactionCount,
    amountEur: report.totals.amountEur,
    percentage: report.totals.percentage / 100,
  });
  total.font = { bold: true };
  summary.getColumn('amountEur').numFmt = '#,##0.00';
  summary.getColumn('percentage').numFmt = '0.00%';

  const details = workbook.addWorksheet('Transactions');
  details.columns = [
    { header: 'Expense Category', key: 'category', width: 30 },
    { header: 'Date', key: 'date', width: 14 },
    { header: 'Counterparty / Description', key: 'description', width: 50 },
    { header: 'Original Amount', key: 'originalAmount', width: 18 },
    { header: 'Currency', key: 'currency', width: 12 },
    { header: 'Amount EUR', key: 'amountEur', width: 18 },
  ];
  details.getRow(1).font = { bold: true };
  report.rows.forEach((row) =>
    row.transactions.forEach((transaction) =>
      details.addRow({
        category: row.category,
        date: transaction.date.slice(0, 10),
        description: transaction.counterpartyDescription,
        originalAmount: transaction.originalAmount,
        currency: transaction.currency,
        amountEur: transaction.amountEur,
      })
    )
  );
  details.getColumn('originalAmount').numFmt = '#,##0.00';
  details.getColumn('amountEur').numFmt = '#,##0.00';

  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcelWorkbook(buffer, `Expenses_By_Category_${fileDate()}.xlsx`);
};

export const exportExpenseCategoryReportPdf = async (
  report: ExpenseCategoryReportResult,
  filters: string[]
): Promise<void> => {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(16);
  doc.text('Expenses by Category', 14, 14);
  doc.setFontSize(9);
  filters.forEach((filter, index) => doc.text(filter, 14, 21 + index * 5));
  autoTable(doc, {
    startY: 24 + filters.length * 5,
    head: [['Expense Category', 'Transactions', 'Amount EUR', '% of Total Expenses']],
    body: [
      ...report.rows.map((row) => [
        `${row.category}${row.isInactive ? ' (Inactive)' : ''}`,
        String(row.transactionCount),
        row.amountEur.toFixed(2),
        `${row.percentage.toFixed(2)}%`,
      ]),
      [
        'TOTAL',
        String(report.totals.transactionCount),
        report.totals.amountEur.toFixed(2),
        `${report.totals.percentage.toFixed(2)}%`,
      ],
    ],
    headStyles: { fillColor: [66, 139, 202] },
  });

  report.rows.forEach((row) => {
    doc.addPage();
    doc.setFontSize(13);
    doc.text(`${row.category} — ${row.amountEur.toFixed(2)} EUR`, 14, 14);
    autoTable(doc, {
      startY: 20,
      head: [['Date', 'Counterparty / Description', 'Original Amount', 'Currency', 'Amount EUR']],
      body: [
        ...row.transactions.map((transaction) => [
          transaction.date.slice(0, 10),
          transaction.counterpartyDescription,
          transaction.originalAmount.toFixed(2),
          transaction.currency,
          transaction.amountEur.toFixed(2),
        ]),
        ['', 'TOTAL', '', '', row.amountEur.toFixed(2)],
      ],
      headStyles: { fillColor: [66, 139, 202] },
      styles: { fontSize: 8 },
    });
  });

  doc.save(`Expenses_By_Category_${fileDate()}.pdf`);
};
